import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CustomTextArea from './CustomTextArea';
import blobStorageService from '../services/blobStorage';
import apiService from '../services/api';
import { snapshotClientErrors } from '../utils/clientErrorLog';

const MAX_IMAGES = 5;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

const primaryButtonSx = {
  backgroundColor: 'var(--color-accent)',
  color: '#fff',
  textTransform: 'none',
  '&:hover': { backgroundColor: 'var(--color-accent)', filter: 'brightness(1.08)' }
};

const outlinedButtonSx = {
  color: 'var(--color-accent)',
  borderColor: 'var(--color-accent)',
  textTransform: 'none'
};

function isImageFile(file) {
  return !!file && IMAGE_TYPES.includes(String(file.type || '').toLowerCase());
}

const BugReportDialog = ({ open, captureFile, captureError, onClose, onSubmitted }) => {
  const fileInputRef = useRef(null);
  const imagesRef = useRef([]);
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [logCount, setLogCount] = useState(0);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  imagesRef.current = images;

  useEffect(() => {
    if (!open) return undefined;
    const owned = [];
    if (captureFile) {
      const url = URL.createObjectURL(captureFile);
      owned.push(url);
      setImages([{
        id: 'auto',
        file: captureFile,
        url,
        isAutoCapture: true
      }]);
    } else {
      setImages([]);
    }
    setDescription('');
    setFormError('');
    setLogCount(snapshotClientErrors().length);
    setSubmitting(false);
    return () => {
      const urls = new Set(owned);
      imagesRef.current.forEach((image) => {
        if (image.url) urls.add(image.url);
      });
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [open, captureFile]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter(isImageFile);
    if (!incoming.length) {
      setFormError('Use a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    const room = MAX_IMAGES - images.length;
    if (room <= 0) {
      setFormError(`You can attach up to ${MAX_IMAGES} images.`);
      return;
    }
    const accepted = incoming.slice(0, room).map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      file,
      url: URL.createObjectURL(file),
      isAutoCapture: false
    }));
    if (incoming.length > room) {
      setFormError(`Only ${MAX_IMAGES} images can be attached. Extra images were left off.`);
    } else {
      setFormError('');
    }
    setImages((current) => [...current, ...accepted]);
  };

  const removeImage = (id) => {
    setImages((current) => {
      const target = current.find((image) => image.id === id);
      if (target?.url) URL.revokeObjectURL(target.url);
      return current.filter((image) => image.id !== id);
    });
  };

  const requestClose = () => {
    if (submitting) return;
    const dirty = description.trim() || images.length > 0;
    if (dirty && !window.confirm('Close this bug report without sending it?')) return;
    onClose();
  };

  const handlePaste = (event) => {
    const files = Array.from(event.clipboardData?.files || []);
    if (!files.some(isImageFile)) return;
    event.preventDefault();
    addFiles(files);
  };

  const handleSubmit = async () => {
    const trimmed = description.trim();
    if (!trimmed) {
      setFormError('Describe what went wrong.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const screenshots = [];
      for (let index = 0; index < images.length; index += 1) {
        const image = images[index];
        const uploaded = await blobStorageService.uploadFileToContainer(image.file, 'bug-reports');
        const data = uploaded?.data || {};
        if (!data.blobName) {
          throw new Error('Screenshot upload did not return a file name.');
        }
        screenshots.push({
          filename: data.blobName,
          fileContainer: 'bug-reports',
          originalFilename: data.originalName || image.file.name,
          contentType: data.contentType || image.file.type,
          sortOrder: index,
          isAutoCapture: image.isAutoCapture
        });
      }

      const response = await apiService.createBugReport({
        description: trimmed,
        pageUrl: `${window.location.pathname}${window.location.search}`,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        errorLog: snapshotClientErrors(),
        screenshots
      });
      onSubmitted(response?.data);
    } catch (error) {
      setFormError(error.message || 'Could not send the bug report.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        className: 'bug-report-modal-content',
        sx: {
          background: 'var(--color-bg-alt)',
          color: 'var(--color-text)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--color-text)', pr: 6 }}>
        Report a bug
        <IconButton
          aria-label="Close"
          onClick={requestClose}
          disabled={submitting}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'var(--color-text)' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent onPaste={handlePaste}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {captureError ? <Alert severity="warning">{captureError}</Alert> : null}
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <CustomTextArea
            label="What went wrong?"
            required
            rows={5}
            value={description}
            onChange={setDescription}
            placeholder="What were you doing, and what did you expect to happen?"
            disabled={submitting}
          />
          <Box sx={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
            {logCount === 1
              ? '1 recent console message from this session will be included.'
              : `${logCount} recent console messages from this session will be included.`}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ fontSize: 14, fontWeight: 500 }}>Screenshots</Box>
              <Button
                variant="outlined"
                size="small"
                sx={outlinedButtonSx}
                disabled={submitting || images.length >= MAX_IMAGES}
                onClick={() => fileInputRef.current?.click()}
              >
                Add image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_TYPES.join(',')}
                multiple
                hidden
                onChange={(event) => {
                  addFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            </Box>
            <Box sx={{ color: 'var(--color-text-secondary)', fontSize: 13, mb: 1 }}>
              Paste a screenshot with Ctrl+V, or add a file. Up to {MAX_IMAGES} images.
            </Box>
            {images.length === 0 ? (
              <Box sx={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>
                No screenshot attached.
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {images.map((image) => (
                  <Box
                    key={image.id}
                    sx={{
                      width: 180,
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: 'var(--color-bg-paper)'
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.isAutoCapture ? 'Captured page' : image.file.name}
                      style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5, gap: 1 }}>
                      <Box sx={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {image.isAutoCapture ? 'Captured page' : 'Added image'}
                      </Box>
                      <Button
                        size="small"
                        onClick={() => removeImage(image.id)}
                        disabled={submitting}
                        sx={{ textTransform: 'none', minWidth: 0, color: 'var(--color-text)' }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={requestClose} disabled={submitting} sx={{ textTransform: 'none', color: 'var(--color-text)' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={primaryButtonSx}>
          {submitting ? 'Sending…' : 'Send report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BugReportDialog;
